"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../../../navigation/Layout/Layout";

import PharmacyHeader from "../../components/PharmacyHeader/PharmacyHeader";
import PharmacyList from "../../components/PharmacyList/PharmacyList";

import usePlaces from "../../hooks/usePlaces";
import { useTour } from "../../../appointments/hooks/useTour";
import { kineTourSteps } from "./kineTourConfig";

import "./Kine.css";

export default function Kine() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [shouldStartTour, setShouldStartTour] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(20); // ⬅️ Show first 20 initially

  const listRef = useRef(null);

  const {
    items,
    loading,
    error,
    loadCategory,
    selectPlace,
  } = usePlaces();

  // Guided tour
  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "kine";

    if (hasGuide || hasTour) {
      setTimeout(() => {
        setShouldStartTour(true);
      }, 1000);
    }
  }, [searchParams]);

  useTour(shouldStartTour, kineTourSteps);

  // Load physiotherapists
  useEffect(() => {
    loadCategory("physiotherapists", { limit: 200, skip: 0 }); // get enough data for pagination
  }, [loadCategory]);

  // Filter + pagination
  const filtered = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const q = searchQuery.trim().toLowerCase();

    if (!q) {
      return list.slice(0, visibleCount); // ⬅️ pagination
    }

    // Searching always uses full list
    return list.filter(
      (k) =>
        (k.name || "").toLowerCase().includes(q) ||
        (k.address || "").toLowerCase().includes(q) ||
        (k.vicinity || "").toLowerCase().includes(q)
    );
  }, [items, searchQuery, visibleCount]);

  // Infinite scroll
  useEffect(() => {
    const div = listRef.current;
    if (!div) return;

    const onScroll = () => {
      if (loading) return;

      if (div.scrollTop + div.clientHeight >= div.scrollHeight - 40) {
        setVisibleCount((prev) => prev + 20); // load 20 more
      }
    };

    div.addEventListener("scroll", onScroll);
    return () => div.removeEventListener("scroll", onScroll);
  }, [loading]);

  // Reset pagination when search changes
  useEffect(() => {
    if (searchQuery.trim() !== "") {
      setVisibleCount(200);
    } else {
      setVisibleCount(20);
    }
  }, [searchQuery]);

  return (
    <AppLayout>
      <main className="kine-main kine--listOnly">
        <div className="kine-container">
	          <PharmacyHeader
	            title={t("locators.kine.title")}
	            subtitle={t("locators.kine.subtitle")}
            enableFilters={false}
            showTabs={false}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            activeTab="Kine"
            setActiveTab={() => {}}
          />

          <section className="kine-list-card">
	            <div className="kine-list-scroll" ref={listRef}>
	              {loading && (
	                <p className="kine-empty">{t("locators.kine.loading")}</p>
	              )}

	              {!loading && error && (
	                <p className="kine-empty">
	                  {t("locators.kine.failed", { error: String(error) })}
	                </p>
	              )}

	              {!loading && !error && filtered.length === 0 ? (
	                <p className="kine-empty">{t("locators.kine.empty")}</p>
	              ) : (
                <PharmacyList
                  pharmacies={filtered}
                  onPharmacyClick={(k) => selectPlace(k)}
                  selectedPharmacy={null}
                />
              )}
            </div>
          </section>
        </div>
      </main>
    </AppLayout>
  );
}
